import { useEffect, useState } from 'react';
import { api } from '../api';

interface Account {
  id: string;
  provider: string;
  emailAddress: string;
  accountType: string;
  status: string;
}
interface Message {
  id: string;
  fromAddress: string | null;
  subject: string | null;
  snippet: string | null;
  receivedAt: string | null;
  direction: string;
  isShared: boolean;
  assignedToUserId: string | null;
  matchedContactId: string | null;
}
interface MessageDetail extends Message {
  body: string;
  bodyError?: boolean;
}

export function InboxPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [shared, setShared] = useState(false);
  const [open, setOpen] = useState<MessageDetail | null>(null);
  const [replyHtml, setReplyHtml] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  async function loadAccounts() {
    setAccounts(await api.get<Account[]>('/api/email/accounts').catch(() => []));
  }
  async function loadMessages() {
    setMessages(await api.get<Message[]>(`/api/email/messages${shared ? '?shared=true' : ''}`).catch(() => []));
  }

  useEffect(() => {
    void loadAccounts();
  }, []);
  useEffect(() => {
    void loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared]);

  function connect(provider: string, type: 'personal' | 'shared') {
    let url = `/api/email/connect/${provider}?type=${type}`;
    if (type === 'shared') {
      const address = window.prompt('Shared mailbox address?');
      if (!address) return;
      url += `&address=${encodeURIComponent(address)}`;
    }
    window.location.href = url;
  }

  async function openMessage(id: string) {
    setReplyHtml('');
    setMsg(null);
    setOpen(await api.get<MessageDetail>(`/api/email/messages/${id}`));
  }

  async function sendReply() {
    if (!open) return;
    try {
      await api.post(`/api/email/messages/${open.id}/reply`, { html: replyHtml });
      setMsg('Reply sent from your mailbox.');
      setReplyHtml('');
    } catch {
      setMsg('Reply failed — connect your personal mailbox first.');
    }
  }

  async function claim() {
    if (!open) return;
    try {
      await api.post(`/api/email/messages/${open.id}/claim`);
      setMsg('Claimed. Other reps will see it is yours.');
      await loadMessages();
    } catch {
      setMsg('Could not claim (already claimed?).');
    }
  }

  return (
    <div className="card">
      <h1>Inbox</h1>

      <div className="row" style={{ flexWrap: 'wrap' }}>
        <button className="btn" onClick={() => connect('microsoft', 'personal')}>Connect Microsoft</button>
        <button className="btn" onClick={() => connect('google', 'personal')}>Connect Google</button>
        <button className="btn" onClick={() => connect('microsoft', 'shared')}>Connect shared (MS)</button>
        <button className="btn" onClick={() => connect('google', 'shared')}>Connect shared (Google)</button>
      </div>
      <div className="row" style={{ marginTop: '0.5rem' }}>
        {accounts.map((a) => (
          <span key={a.id} className="tag">{a.accountType}: {a.emailAddress} ({a.status})</span>
        ))}
        {accounts.length === 0 && <span className="muted">No mailboxes connected yet.</span>}
      </div>

      <div className="row" style={{ marginTop: '1rem' }}>
        <button className={shared ? 'btn' : 'btn btn-primary'} onClick={() => setShared(false)}>Personal</button>
        <button className={shared ? 'btn btn-primary' : 'btn'} onClick={() => setShared(true)}>Shared</button>
      </div>

      {msg && <p style={{ color: '#1d4ed8' }}>{msg}</p>}

      <table className="grid">
        <thead><tr><th>From</th><th>Subject</th><th>When</th><th></th></tr></thead>
        <tbody>
          {messages.map((m) => (
            <tr key={m.id}>
              <td>{m.fromAddress ?? '—'}{m.matchedContactId ? ' ✓contact' : ''}</td>
              <td>{m.subject ?? '(no subject)'}</td>
              <td>{m.receivedAt ? new Date(m.receivedAt).toLocaleString() : ''}</td>
              <td><button className="btn" onClick={() => void openMessage(m.id)}>Open</button></td>
            </tr>
          ))}
          {messages.length === 0 && <tr><td colSpan={4} className="empty">No messages. Connect a mailbox; the sync service pulls incoming mail.</td></tr>}
        </tbody>
      </table>

      {open && (
        <div className="overlay" onClick={() => setOpen(null)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2>{open.subject ?? '(no subject)'}</h2>
              <button className="btn" onClick={() => setOpen(null)}>Close</button>
            </div>
            <p className="muted">From {open.fromAddress ?? '—'}</p>
            {open.isShared && (
              <button className="btn" onClick={() => void claim()}>
                {open.assignedToUserId ? 'Claimed' : 'Claim this message'}
              </button>
            )}
            <div className="card" style={{ marginTop: '0.75rem' }} dangerouslySetInnerHTML={{ __html: open.body || open.snippet || '' }} />
            <h3 style={{ fontSize: '0.95rem', marginTop: '1rem' }}>Reply (from your mailbox)</h3>
            <textarea value={replyHtml} onChange={(e) => setReplyHtml(e.target.value)} placeholder="<p>Your reply…</p>" />
            <button className="btn btn-primary" onClick={() => void sendReply()} disabled={!replyHtml}>Send reply</button>
          </div>
        </div>
      )}
    </div>
  );
}
